import React, { useState, useCallback } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useIntl } from 'react-intl';

import Typography from '@mui/material/Typography';

import BaseExerciseQuestionsForm, { useQuestionsCountTextField } from 'modules/exercises/components/BaseExerciseQuestionForm';

import { FillInTheBlanksExerciseInput, FillInTheBlanksQuestionInput } from '../../types/form';

import FillInTheBlanksQuestionField from '../FillInTheBlanksQuestionField';

interface FillInTheBlanksQuestionsFormProps {
  getNextQuestion: (index: number) => FillInTheBlanksQuestionInput;
}

const FillInTheBlanksQuestionsForm = (props: FillInTheBlanksQuestionsFormProps) => {
  const { getNextQuestion } = props;
  const { control } = useFormContext<FillInTheBlanksExerciseInput>();
  const { fields, append, remove } = useFieldArray({ control, name: 'questions' });
  const intl = useIntl();
  const questionsCount = fields.length;
  const [questionsCountTextFieldValue, setQuestionsCountTextFieldValue] = useState(`${questionsCount}`);

  const handleAddQuestion = useCallback(() => {
    append(getNextQuestion(fields.length));
    setQuestionsCountTextFieldValue(`${fields.length + 1}`);
  }, [append, getNextQuestion, fields]);

  const handleRemoveQuestionAtIndex = useCallback(
    (index: number | number[] | undefined) => {
      remove(index);
      setQuestionsCountTextFieldValue(`${fields.length - 1}`);
    },
    [remove, fields]
  );

  const { onChangeQuestionsCount } = useQuestionsCountTextField({
    onAdd: append,
    onRemove: remove,
    getQuestionToAppend: getNextQuestion,
    questionsCount: fields.length,
    onChangeQuestionsCountTex: setQuestionsCountTextFieldValue,
  });

  return (
    <BaseExerciseQuestionsForm
      onAddQuestion={handleAddQuestion}
      onChangeQuestionCount={onChangeQuestionsCount}
      questionsCountText={questionsCountTextFieldValue}
    >
      <Typography mb={1} variant="body1">
        {intl.formatMessage(
          { id: 'exercise.fillInTheBlanks.explanation' },
          {
            strong: (chunks) => <strong>{chunks}</strong>,
            br: <br />,
          }
        )}
      </Typography>
      {fields.map((field, index) => (
        <FillInTheBlanksQuestionField key={field.id} index={index} onRemove={handleRemoveQuestionAtIndex} />
      ))}
    </BaseExerciseQuestionsForm>
  );
};

export default FillInTheBlanksQuestionsForm;
